import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import { isolatedCloudDomain } from '@atlaskit/atlassian-context/isolated-cloud-domain';
import { ProductEnvironment } from '@atlassian/forge-ui-types/environments';
import type { ExtensionViewData } from '@atlassian/forge-ui-types/public/effect';

import { getForgeCDNBaseDomain } from '../../../utils';
import { getForgeAppCdnSubdomain } from '../../../utils/forgeAppCdnOrigin';
import type { Extension } from '../../../web-client';
import { getEncodedAppContext } from './getEncodedAppContext';

const SAFE_RESOURCE_ENTRY_PATTERN = /^[\w][\w.-]*$/;

/**
 * Appends `resolvedResourceEntry` to a base URL after validating it is a
 * safe resource entry file name. Throws if the value is present but invalid.
 */
export const appendResolvedResourceEntry = (
	baseUrl: string,
	resolvedResourceEntry: string | undefined,
): string => {
	if (resolvedResourceEntry === undefined) {
		return baseUrl;
	}
	if (!SAFE_RESOURCE_ENTRY_PATTERN.test(resolvedResourceEntry)) {
		throw new Error('Invalid resource entry specified.');
	}
	return `${baseUrl}${resolvedResourceEntry}`;
};

export const productEnvToCdnEnv: {
	DEVELOPMENT: string;
	PRODUCTION: string;
	STAGING: string;
} = {
	[ProductEnvironment.PRODUCTION]: 'prod',
	[ProductEnvironment.STAGING]: 'stg',
	[ProductEnvironment.DEVELOPMENT]: 'stg',
};

export const createSrcFromExtension = (
	appId: string,
	extension: Extension,
	environment: ProductEnvironment,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
	resolvedResourceEntry?: string,
): string => {
	return isIsolatedCloud()
		? createSrcFromExtensionForIC(
				appId,
				extension,
				environment,
				entryPoint,
				extensionViewData,
				resolvedResourceEntry,
			)
		: createSrcFromExtensionForCommercialAndFedRamp(
				appId,
				extension,
				environment,
				entryPoint,
				extensionViewData,
				resolvedResourceEntry,
			);
};

export const createSrcFromExtensionForCommercialAndFedRamp = (
	appId: string,
	extension: Extension,
	environment: ProductEnvironment,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
	resolvedResourceEntry?: string,
): string => {
	const { installationId, environmentId, appVersion, properties } = extension;
	const resourceUploadId = properties.resourceUploadId || appVersion;

	const domain = `${getForgeAppCdnSubdomain(installationId, appId)}.${getForgeCDNBaseDomain(
		environment,
	)}`;

	const encodedAppContext = getEncodedAppContext(extension, extensionViewData, environment);

	const urlSafeContext = urlSafeB64(encodedAppContext);

	const baseUrl = `https://${domain}/${appId}/${environmentId}/${resourceUploadId}/${entryPoint}/_ctx_${urlSafeContext}/`;
	return appendResolvedResourceEntry(baseUrl, resolvedResourceEntry);
};

export const createSrcFromExtensionForIC = (
	appId: string,
	extension: Extension,
	environment: ProductEnvironment,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
	resolvedResourceEntry?: string,
): string => {
	const { installationId, environmentId, appVersion, properties } = extension;
	const resourceUploadId = properties.resourceUploadId || appVersion;
	const icDomain = isolatedCloudDomain();

	const domain = `${getForgeAppCdnSubdomain(installationId, appId)}.forge-cdn.${icDomain}`;

	const encodedAppContext = getEncodedAppContext(extension, extensionViewData, environment);

	const urlSafeContext = urlSafeB64(encodedAppContext);

	const baseUrl = `https://${domain}/forge-cdn/${appId}/${environmentId}/${resourceUploadId}/${entryPoint}/_ctx_${urlSafeContext}/`;
	return appendResolvedResourceEntry(baseUrl, resolvedResourceEntry);
};

export function urlSafeB64(str: string): string {
	return str
		.replace(/\+/g, '-') // Replace '+' with '-'
		.replace(/\//g, '_') // Replace '/' with '_'
		.replace(/=+$/, ''); // Remove padding
}
